import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import { PageShell } from './layout';
import { useStelline } from '../contexts/StellineContext';
import useHasEverAuthenticated from '../hooks/useHasEverAuthenticated';
import { buildTrackedAuthRedirectUrl } from '../analytics';
import Button from './Button';
import FaIcon from './FaIcon';
import styles from '../styles/GettonciniLandingPage.module.css';
import heroStyles from '../styles/StellineLandingHero.module.css';
import buttonStyles from '../styles/Button.module.css';

const HeroStellineIcon = ({ size = 120, style = {}, className = '' }) => {
  const uniqueId = React.useId();
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      style={style}
    >
      <defs>
        <clipPath id={`stelline-hero-clip-${uniqueId}`}>
          <circle cx="12" cy="12" r="10" />
        </clipPath>
        <linearGradient id={`stelline-hero-gradient-${uniqueId}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#fde68a" />
          <stop offset="100%" stopColor="#f59e0b" />
        </linearGradient>
        <filter id={`stelline-hero-glow-${uniqueId}`} x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="1" result="coloredBlur" />
          <feMerge>
            <feMergeNode in="coloredBlur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
      <circle
        cx="12"
        cy="12"
        r="10"
        fill={`url(#stelline-hero-gradient-${uniqueId})`}
        stroke="#b45309"
        strokeWidth="1"
        filter={`url(#stelline-hero-glow-${uniqueId})`}
      />
      <g clipPath={`url(#stelline-hero-clip-${uniqueId})`}>
        <circle cx="12" cy="12" r="7" fill="#fef3c7" opacity="0.3" />
        <polygon
          points="12,5 14.2,9.3 19,10 15.5,13.4 16.3,18.2 12,15.9 7.7,18.2 8.5,13.4 5,10 9.8,9.3"
          fill="#fff7ed"
        />
      </g>
    </svg>
  );
};

const EARNING_METHODS = [
  {
    icon: 'fa-gamepad',
    title: 'Completa le partite',
    description: 'Le stelline premiano i traguardi che raggiungi giocando con costanza.',
    badge: 'Gioca'
  },
  {
    icon: 'fa-trophy',
    title: 'Raggiungi obiettivi',
    description: 'Quando superi obiettivi importanti, il tuo totale stelline cresce.',
    badge: 'Traguardi'
  },
  {
    icon: 'fa-chart-line',
    title: 'Consolida abitudini',
    description: 'Giocare ogni giorno, usare aiutini e imparare con Lella sono buone abitudini. Stelline extra ti premiano per questo.',
    badge: 'Progressi'
  }
];

const PURPOSE_CARDS = [
  {
    emoji: '🎯',
    title: 'Misurano i traguardi',
    description: 'Le stelline sono un indicatore dei risultati che hai raggiunto nei giochi.'
  },
  {
    emoji: '🏆',
    title: 'Celebrano i progressi',
    description: 'Ogni stellina rappresenta un passo avanti nel tuo percorso di apprendimento.'
  },
  {
    emoji: '📅',
    title: 'Alimentano la classifica',
    description: 'Per la classifica contano solo le stelline guadagnate nella settimana in corso.'
  }
];

const StellineLandingPage = () => {
  const { isSignedIn } = useUser();
  const hasEverAuthenticated = useHasEverAuthenticated();
  const navigate = useNavigate();
  const { stellineBalance } = useStelline();

  const displayBalance = isSignedIn ? Math.max(0, Number(stellineBalance) || 0) : 0;

  const handleAuthClick = () => {
    const authUrl = buildTrackedAuthRedirectUrl({
      cta_kind: hasEverAuthenticated ? 'sign_in' : 'sign_up',
      source_surface: 'stelline_landing',
      gate_reason: 'stelline_landing',
      next_path: '/stelline',
      is_returning_device: hasEverAuthenticated,
      track_gate_viewed: true,
      trigger: 'landing_cta',
    });
    navigate(authUrl);
  };

  return (
    <PageShell>
      <section className={heroStyles.heroSection}>
        <div className={heroStyles.constellationField}>
          {[...Array(12)].map((_, i) => (
            <span key={i} className={heroStyles.constellationDot} />
          ))}
        </div>

        <div className={heroStyles.orbitingStars}>
          {[...Array(6)].map((_, i) => (
            <div key={i} className={heroStyles.orbitStar}>
              <HeroStellineIcon size={30 + (i % 3) * 15} />
            </div>
          ))}
        </div>

        <div className={heroStyles.heroHalo} />

        <div className={heroStyles.heroContent}>
          <span className={heroStyles.heroTag}>I tuoi traguardi</span>

          <div className={heroStyles.iconShowcase}>
            <div className={heroStyles.mainIcon}>
              <HeroStellineIcon size={120} />
            </div>
          </div>

          <h1 className={heroStyles.heroTitle}>Stelline</h1>

          <p className={heroStyles.heroSubtitle}>
            Le stelline premiano i tuoi traguardi nei Giochini di Parole.
            Si ottengono giocando e mostrano i tuoi progressi nel tempo.
          </p>

          {isSignedIn ? (
            <div className={heroStyles.balanceCard}>
              <span className={heroStyles.balanceLabel}>Il tuo totale stelline</span>
              <div className={heroStyles.balanceValue}>
                <span className={heroStyles.balanceNumber}>{displayBalance.toLocaleString('it-IT')}</span>
                <HeroStellineIcon size={36} />
              </div>
              <Link to="/" className={heroStyles.playButton}>
                <FaIcon className="fas fa-gamepad" />
                <span>Vai a giocare</span>
              </Link>
            </div>
          ) : (
            <div className={heroStyles.ctaContainer}>
              <Button
                primary
                className={`${heroStyles.ctaButton} ${buttonStyles.primaryBlue}`}
                onClick={handleAuthClick}
              >
                {hasEverAuthenticated ? 'Accedi per vedere le tue stelline' : 'Inizia a ottenere stelline'}
              </Button>
              <p className={heroStyles.ctaNote}>
                Registrazione gratuita • Salvataggio su tutti i dispositivi
              </p>
            </div>
          )}
        </div>

        <div className={heroStyles.scrollIndicator}>
          <span>Scopri di piu</span>
          <FaIcon className={`fas fa-chevron-down ${heroStyles.scrollArrow}`} />
        </div>
      </section>

      <section className={styles.whatSection}>
        <div className={styles.sectionContainer}>
          <h2 className={styles.sectionTitle}>Cosa sono le stelline?</h2>
          <p className={styles.sectionDescription}>
            Le stelline sono un riconoscimento dei tuoi risultati: indicano i traguardi
            che hai raggiunto e la tua costanza nel giocare. A differenza dei gettoncini,
            non si spendono: restano come storico dei tuoi progressi.
          </p>

          <div className={styles.keyPoints}>
            <div className={styles.keyPoint}>
              <div className={styles.keyPointIcon}>
                <FaIcon className="fas fa-star" />
              </div>
              <h3>Sono premi</h3>
              <p>Le stelline non si comprano e non si spendono.</p>
            </div>
            <div className={styles.keyPoint}>
              <div className={styles.keyPointIcon}>
                <FaIcon className="fas fa-chart-line" />
              </div>
              <h3>Misurano il progresso</h3>
              <p>Rendono visibile il tuo percorso nei giochi giorno dopo giorno.</p>
            </div>
            <div className={styles.keyPoint}>
              <div className={styles.keyPointIcon}>
                <FaIcon className="fas fa-user-check" />
              </div>
              <h3>Legate al tuo account</h3>
              <p>Con un account restano salvate e sincronizzate sui tuoi dispositivi.</p>
            </div>
          </div>
        </div>
      </section>

      <section className={styles.earnSection}>
        <div className={styles.sectionContainer}>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionEmoji}>🏆</span>
            <h2 className={styles.sectionTitle}>Come si ottengono</h2>
            <p className={styles.sectionSubtitle}>
              Le stelline crescono insieme ai tuoi risultati.
            </p>
          </div>

          <div className={styles.earningGrid}>
            {EARNING_METHODS.map((method, index) => (
              <div key={index} className={styles.earningCard}>
                <div
                  className={styles.earningIconWrapper}
                  style={{ background: 'linear-gradient(145deg, #f59e0b, #d97706)' }}
                >
                  <FaIcon className={`fas ${method.icon}`} />
                </div>
                <div className={styles.earningContent}>
                  <h3 className={styles.earningTitle}>{method.title}</h3>
                  <p className={styles.earningDescription}>{method.description}</p>
                </div>
                <div className={styles.earningAmount}>
                  <span className={styles.amountValue}>{method.badge}</span>
                  <HeroStellineIcon size={20} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className={styles.spendSection}>
        <div className={styles.sectionContainer}>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionEmoji}>✨</span>
            <h2 className={styles.sectionTitle}>Perche esistono</h2>
            <p className={styles.sectionSubtitle}>
              Le stelline non sono una valuta: sono il tuo indicatore di progresso e la base della classifica settimanale.
            </p>
          </div>

          <div className={styles.spendingGrid}>
            {PURPOSE_CARDS.map((item, index) => (
              <div key={index} className={styles.spendingCard}>
                <span className={styles.spendingEmoji}>{item.emoji}</span>
                <h3 className={styles.spendingTitle}>{item.title}</h3>
                <p className={styles.spendingDescription}>{item.description}</p>
              </div>
            ))}
          </div>

          <div className={styles.spendingNote}>
            <FaIcon className="fas fa-info-circle" />
            <p>
              Vuoi usare funzioni premium durante le partite? Per quello servono i
              gettoncini. Le stelline, invece, celebrano i tuoi traguardi.
            </p>
          </div>

          <div className={styles.spendingNote}>
            <FaIcon className="fas fa-trophy" />
            <p>
              La classifica Stelline si azzera ogni settimana la domenica alle 00:00 UTC. Manteniamo comunque lo storico delle tue stelline accumulate.
            </p>
          </div>
        </div>
      </section>

      <section className={styles.faqSection}>
        <div className={styles.sectionContainer}>
          <h2 className={styles.sectionTitle}>Domande frequenti</h2>

          <div className={styles.faqGrid}>
            <div className={styles.faqItem}>
              <h3>Le stelline si possono spendere?</h3>
              <p>
                No. Le stelline sono solo un indicatore dei tuoi traguardi,
                non una valuta da usare nelle partite.
              </p>
            </div>
            <div className={styles.faqItem}>
              <h3>Posso comprare stelline?</h3>
              <p>
                No, non si acquistano: si ottengono giocando e raggiungendo risultati.
              </p>
            </div>
            <div className={styles.faqItem}>
              <h3>Dove vedo le mie stelline?</h3>
              <p>
                Le trovi nella barra in alto e nella sezione account, sempre aggiornate
                quando giochi con il tuo profilo.
              </p>
            </div>
            <div className={styles.faqItem}>
              <h3>Che differenza c'e tra stelline e serie?</h3>
              <p>
                La <Link to="/serie">serie</Link> misura i giorni consecutivi,
                mentre le stelline riassumono i traguardi raggiunti nei giochi.
              </p>
            </div>
            <div className={styles.faqItem}>
              <h3>Quando si resetta la classifica Stelline?</h3>
              <p>
                Ogni domenica alle 00:00 UTC. Da quel momento la classifica riparte da zero
                e conta solo le stelline guadagnate nella nuova settimana.
              </p>
            </div>
            <div className={styles.faqItem}>
              <h3>Perdo lo storico quando la classifica si azzera?</h3>
              <p>
                No. Il ranking settimanale si azzera, ma lo storico delle stelline guadagnate
                resta tracciato per vedere i tuoi progressi nel tempo.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className={styles.bottomCta}>
        <div className={styles.bottomCtaContent}>
          <HeroStellineIcon size={80} className={styles.bottomCtaIcon} />
          <h2 className={styles.bottomCtaTitle}>
            {isSignedIn ? 'Continua a collezionare stelline!' : 'Pronto a iniziare?'}
          </h2>
          <p className={styles.bottomCtaText}>
            {isSignedIn
              ? 'Gioca ogni giorno e trasforma i traguardi in nuove stelline.'
              : 'Crea un account gratuito e inizia a salvare i tuoi progressi.'
            }
          </p>

          {isSignedIn ? (
            <Link to="/" className={styles.bottomCtaButton}>
              <FaIcon className="fas fa-gamepad" />
              <span>Vai ai giochi</span>
            </Link>
          ) : (
            <button className={styles.bottomCtaButton} onClick={handleAuthClick}>
              <FaIcon className="fas fa-user-plus" />
              <span>{hasEverAuthenticated ? 'Accedi' : 'Registrati gratis'}</span>
            </button>
          )}
        </div>
      </section>
    </PageShell>
  );
};

export default StellineLandingPage;
